import React, { useState } from 'react';
import { observer } from 'mobx-react';
import {
    Box,
    Typography,
    IconButton,
    Collapse,
    useTheme
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FilledButton from './buttons/FilledButton';
import EmptyState from './state/EmptyState';

function FormSetPanel({ 
    formSet, 
    addButtonTitle, 
    addButtonIcon, 
    onAddItem, 
    onRemoveItem, 
    renderItemContent, 
    getItemTitle, 
    emptyStateTitle, 
    emptyStateDescription, 
    emptyStateIcon,
    disabled = false
}) {
    const theme = useTheme();
    const [expandedItems, setExpandedItems] = useState({});
    const style = styles(theme);

    const toggleItemExpansion = (itemId) => {
        setExpandedItems(prev => ({
            ...prev,
            [itemId]: !prev[itemId]
        }));
    };

    const addItem = () => {
        const newItem = onAddItem();
        setExpandedItems(prev => ({
            ...prev,
            [newItem.formId]: true
        }));
    };

    const removeItem = (itemId) => {
        onRemoveItem(itemId);
        setExpandedItems(prev => {
            const newState = { ...prev };
            delete newState[itemId];
            return newState;
        });
    };

    const renderItem = (id, form) => {
        const isExpanded = expandedItems[id];
        
        return (
            <Box key={id} sx={style.itemContainer}>
                <Box sx={style.itemHeader} onClick={() => toggleItemExpansion(id)}>
                    <Box sx={style.itemHeaderLeft}>
                        <IconButton size="small">
                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                        <Typography variant="h6">
                            {getItemTitle(form)}
                        </Typography>
                    </Box>
                    {!disabled && <IconButton
                        color="error"
                        onClick={(e) => {
                            e.stopPropagation();
                            removeItem(id);
                        }}
                        size="small"
                    >
                        <DeleteIcon />
                    </IconButton>}
                </Box>

                <Collapse in={isExpanded}>
                    <Box sx={style.itemContent}>
                        {renderItemContent(form)}
                    </Box>
                </Collapse>
            </Box>
        );
    };

    const content = () => {
        return (
            <div style={{ paddingTop: '1.5rem', paddingBottom: '1.5rem' }}>
                {!disabled && <Box sx={style.addItemContainer}>
                    <FilledButton onClick={addItem}>
                        {addButtonIcon}
                        {addButtonTitle}
                    </FilledButton>
                </Box>}

                {formSet.forms.length === 0 ? (
                    <Box sx={style.emptyState} onClick={disabled ? undefined : addItem}>
                        <EmptyState
                            icon={emptyStateIcon}
                            title={emptyStateTitle}
                            description={emptyStateDescription}
                        />
                    </Box>
                ) : (
                    <Box>
                        {Object.entries(formSet.formsPerId).map(([id, form]) => renderItem(id, form))}
                    </Box>
                )}
            </div>
        )
    }

    return content();
}

const styles = (theme) => ({
    addItemContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: '2rem',
        paddingRight: '1rem'
    },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '3rem',
        margin: '0 1rem',
        border: `2px dashed ${theme.palette.divider}`,
        borderRadius: '8px',
        backgroundColor: theme.palette.background.default
    },
    itemContainer: {
        marginBottom: '1.5rem',
        marginLeft: '1rem',
        marginRight: '1rem',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '8px',
        backgroundColor: theme.palette.background.paper
    },
    itemHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1rem 1.5rem',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.action.hover
        }
    },
    itemHeaderLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem'
    },
    itemContent: {
        padding: '1.5rem'
    }
});

export default observer(FormSetPanel);
